import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TrainingProgressService } from '../../services/training-progress.service';
import { ConfettiService } from '../../services/confetti.service';
import { SafeHtmlPipe } from '../../shared/pipes/safe-html.pipe';
import { Icons } from '../../shared/icons';
import { QuizResult } from '../quiz/quiz.component';
import { TranslatePipe } from '../../../filters/translate.pipe';

@Component({
  selector: 'app-results',
  standalone: true,
  imports: [CommonModule, SafeHtmlPipe, TranslatePipe],
  templateUrl: './results.component.html',
  styleUrls: ['../../training-styles.css'],
  encapsulation: ViewEncapsulation.None
})
export class ResultsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private trainingProgressService = inject(TrainingProgressService);
  private confettiService = inject(ConfettiService);

  readonly Icons = Icons;

  result!: QuizResult;
  scorePercent = 0;
  nextModuleId: string | null = null;
  certificateUnlocked = false;
  passThreshold = 0.8;
  selectedProgramVersionCode = '';

  async ngOnInit(): Promise<void> {
    const moduleId = this.route.snapshot.params['moduleId'];
    this.selectedProgramVersionCode = this.route.snapshot.queryParamMap.get('programVersionCode') || '';
    this.trainingProgressService.setProgramVersionCode(this.selectedProgramVersionCode);

    await this.loadFromServer(moduleId);

    if (!this.result || !this.result.results.length) {
      const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
      this.router.navigate(['/my-training', 'quiz', moduleId], { queryParams });
      return;
    }

    // normalise score (API may return 0-1 or 0-100)
    this.scorePercent = this.result.score > 1 ? Math.round(this.result.score) : Math.round(this.result.score * 100);
    this.nextModuleId = null;
    this.certificateUnlocked = this.trainingProgressService.certificateUnlocked();

    // Confetti for pass
    if (this.result.passed) {
      setTimeout(() => this.confettiService.launch(), 300);
    }
  }

  private async loadFromServer(moduleId: string): Promise<void> {
    try {
      const [detail, quizData] = await Promise.all([
        this.trainingProgressService.loadModuleDetail(moduleId),
        this.trainingProgressService.loadQuizAttempt(moduleId)
      ]);

      const questions = quizData?.questions || detail?.questions || detail?.quiz || [];
      const lastAttempt = quizData?.lastAttempt || quizData || null;
      const answers = lastAttempt?.answers || [];
      const passThreshold = quizData?.passThreshold ?? detail?.passThreshold ?? 0.8;
      const correctCount = lastAttempt?.correctCount ?? answers.filter((a: any) => this.isAnswerCorrect(a, questions)).length;
      const totalQuestions = lastAttempt?.totalQuestions ?? questions.length;

      const computedScore = totalQuestions > 0 ? correctCount / totalQuestions : 0;
      const rawScore = lastAttempt?.score ?? computedScore;
      const score = rawScore > 1 ? rawScore / 100 : rawScore; // normalise to 0-1
      const passed = lastAttempt?.passed ?? (score >= passThreshold);

      this.passThreshold = passThreshold;

      this.result = {
        moduleId,
        results: questions.map((q: any) => {
          const a = answers.find((ans: any) => String(ans.quizQuestionId || ans.questionId) === String(q.id));
          return {
            question: q,
            userAnswer: this.getUserAnswer(a, q),
            isCorrect: this.isAnswerCorrect(a, [q])
          };
        }),
        score,
        correct: correctCount,
        total: totalQuestions,
        passed
      };

    } catch (e) {
      this.result = this.reconstructResults(moduleId);
    }
  }

  private reconstructResults(moduleId: string): QuizResult {
    return {
      moduleId,
      results: [],
      score: 0,
      correct: 0,
      total: 0,
      passed: false
    };
  }

  navigate(path: string): void {
    const parts = path.startsWith('/') ? path.split('/').filter(Boolean) : [path];
    const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
    this.router.navigate(['/my-training', ...parts], { queryParams });
  }

  private getUserAnswer(answer: any, question: any): string | null {
    if (!answer) return null;
    if (answer.selectedAnswerText) return answer.selectedAnswerText;

    const idx = this.getSelectedIndex(answer);
    if (idx === -1 || !Array.isArray(question?.options)) return null;
    if (question.options[idx] !== undefined) return question.options[idx];
    if (idx > 0 && question.options[idx - 1] !== undefined) return question.options[idx - 1];
    return null;
  }

  private getSelectedIndex(answer: any): number {
    if (!answer) return -1;
    const rawValue = answer.selectedIndex ?? answer.selected ?? answer.answer ?? answer.optionIndex ?? -1;
    const parsedValue = Number(rawValue);
    return Number.isInteger(parsedValue) ? parsedValue : -1;
  }

  private isAnswerCorrect(answer: any, questions: any[]): boolean {
    if (!answer) return false;
    if (answer.correct === true || answer.isCorrect === true) return true;
    const idx = this.getSelectedIndex(answer);
    if (idx === -1) return false;
    const q = questions.find(qn => qn.id === (answer.quizQuestionId || answer.questionId));
    return q ? q.correctIndex === idx : false;
  }
}
